import React from 'react';
import { formatStudentName } from '../../../../../utils/common';
import { generateStatus } from '../../../../common/students/utility';
import { isInSchoolEvent } from '../../../../../utils/event';
import { Link, useLocation } from 'react-router-dom';
import { useStateValue } from '../../../../../context/AppContext';
import moment from 'moment';
import IconStatus from '../../../../ui/icons/IconStatus';
import { cbAdminRoleProps } from '../../../../../constants/roles';
import { IGetStudent } from '../../../../../apollo/types';
import AccommodationList from '../../../../common/accommodations/AccommodationList';
import { ResizeContext } from '../../../../../context/ResizeContext';

interface StudentRosterRowProps {
  student: IGetStudent;
  showStudentTestSchedule?: boolean;
}

function StudentRosterRow({ student, showStudentTestSchedule = true }: StudentRosterRowProps) {
  const windowSize = React.useContext(ResizeContext);
  const { prefix } = windowSize;
  // Global App state.
  const { orgEvent, user } = useStateValue();

  // History for current page so we can return to it later.
  const { pathname } = useLocation();
  const scheduleColumnsVisible = showStudentTestSchedule && prefix !== 'xs';
  const examStartTimeLocal =
    scheduleColumnsVisible && student?.examStartTime ? moment(student.examStartTime).local() : null;
  const estimatedExamEndTimeLocal =
    scheduleColumnsVisible && student?.estimatedExamEndTime ? moment(student.estimatedExamEndTime).local() : null;

  function generateStatusColor(student: IGetStudent) {
    // Determine which class to use for Student Status.
    if (student.checkedInRoom || student.checkedInCenter) {
      return 'green3';
    } else if (student.absent || student.deniedEntry) {
      return 'red1';
    }
  }

  function hideColumn(columnName: string) {
    if (!showStudentTestSchedule && (columnName === 'startTime' || columnName === 'estimatedExamEndTime')) {
      return true;
    }

    let hideColumnsForPrefixes;
    if (user?.rid === cbAdminRoleProps.CB_ADMIN_USER_RID) {
      hideColumnsForPrefixes = {
        accommodations: ['xs', 'sm'],
        attendance: ['xs', 'sm'],
        dapTestStatus: ['xs'],
        startTime: ['xs'],
        estimatedExamEndTime: ['xs'],
        lastUpdated: ['xs'],
      };
    } else {
      hideColumnsForPrefixes = {
        accommodations: ['xs', 'sm'],
        attendance: ['xs'],
        dapTestStatus: ['xs'],
        startTime: ['xs'],
        estimatedExamEndTime: ['xs'],
        lastUpdated: ['all'],
      };
    }

    return (
      hideColumnsForPrefixes?.[columnName]?.includes('all') ||
      hideColumnsForPrefixes?.[columnName]?.includes(prefix as string)
    );
  }

  function getColumnClass(columnName: string) {
    function classes() {
      if (user?.rid === cbAdminRoleProps.CB_ADMIN_USER_RID) {
        return {
          studentName: '',
          accommodations: 'student-table-row-info--text',
          attendance: '',
          dapTestStatus: 'student-table-row-info--text',
          startTime: 'student-table-row-info--text',
          estimatedExamEndTime: 'student-table-row-info--text',
          lastUpdated: 'student-table-row-info--last-updated',
        };
      } else {
        return {
          studentName: '',
          accommodations: 'student-table-row-info--text',
          attendance: '',
          dapTestStatus: 'student-table-row-info--text',
          startTime: 'student-table-row-info--text',
          estimatedExamEndTime: 'student-table-row-info--text',
          lastUpdated: '',
        };
      }
    }

    return classes()[columnName];
  }

  return (
    <tr className='student-table-row'>
      <th scope='row' className={getColumnClass('studentName')}>
        {/* student name, regNo, and waitlist */}
        {
          <Link
            className='student-table-row-info--name cb-blue5-color'
            to={'/students/get/' + student?.id}
            state={{
              from: pathname,
            }}
          >
            {formatStudentName(student)}
          </Link>
        }
        {!isInSchoolEvent(orgEvent) ? (
          <div className='student-table-row-info--reg'>
            {student?.displayedRegNo && <span> Reg. no.: {student?.displayedRegNo}</span>}
          </div>
        ) : (
          ''
        )}
        {
          <div className='hidden-sm hidden-md-up student-table-row-info--text'>
            {student?.dapTestStatus?.displayName}
          </div>
        }
        {student?.waitListFlag === 'Y' ? <div className='student-table-row-info--reg'>Waitlisted</div> : ''}
      </th>
      {hideColumn('accommodations') ? null : (
        <td className={getColumnClass('accommodations')}>
          {/* accommodations */}
          {student?.accommodationsArray?.length > 0 ? (
            <ul className='student-table-li-info--accommodations'>
              <AccommodationList accommodations={student?.accommodationsArray} studentId={student?.id} />
            </ul>
          ) : (
            'None'
          )}
        </td>
      )}
      {hideColumn('attendance') ? null : (
        <td className={getColumnClass('attendance')}>
          {/* attendance -- present/absent/denied/etc */}
          <IconStatus color={generateStatusColor(student)} text={generateStatus(student).statusText} />
        </td>
      )}
      {hideColumn('dapTestStatus') ? null : (
        <td className={getColumnClass('dapTestStatus')}>
          {/* dapTestStatus */}
          {student?.dapTestStatus?.displayName}
        </td>
      )}
      {hideColumn('startTime') ? null : (
        <td className={getColumnClass('startTime')}>
          {examStartTimeLocal ? (
            <>
              <div>{examStartTimeLocal.format('h:mm A')}</div>
              <div className='student-table-row-info--reg'>{examStartTimeLocal.format('MMM D, YYYY')}</div>
            </>
          ) : (
            <div className='student-table-row-info--text'>Not Started</div>
          )}
        </td>
      )}
      {hideColumn('estimatedExamEndTime') ? null : (
        <td className={getColumnClass('estimatedExamEndTime')}>
          {estimatedExamEndTimeLocal ? (
            <>
              <div>{estimatedExamEndTimeLocal.format('h:mm A')}</div>
              <div className='student-table-row-info--reg'>{estimatedExamEndTimeLocal.format('MMM D, YYYY')}</div>
            </>
          ) : (
            <div className='student-table-row-info--text'>Not Started</div>
          )}
        </td>
      )}

      {hideColumn('lastUpdated') ? null : (
        <td className={getColumnClass('lastUpdated')}>
          {' '}
          {/* last updated */}
          {moment(student?.updated || undefined)
            .local()
            .format('lll')}
        </td>
      )}
    </tr>
  );
}

export default StudentRosterRow;
